import { memo, ReactNode, useCallback, useEffect, useState } from 'react';
import { Box, Button, Container, Header, Modal, SpaceBetween } from '@amzn/awsui-components-core';
import { ContentNode, Epic } from '@amzn/awscat-pattern-library-service-model';
import ConfirmationModalView from '../v2-common/ConfirmationModalView';
import {
  EPIC_TITLE_MIN_LENGTH,
  EPIC_TITLE_MAX_LENGTH,
  EPIC_DESCRIPTION_MIN_LENGTH,
  EPIC_DESCRIPTION_MAX_LENGTH,
} from '../v2-common/InlineValidations';
import TitleInput from '../v2-common/TitleInput';
import { SortableList, SortableRenderArgs } from '../v2-common/SortableList';
import { PatternEpic } from './PatternEpic';
import { usePatternEpicEditorViewModel } from './PatternEpicEditorViewModel';
import { useSuggestionNavigationService } from '../../contexts/SuggestionNavigationServiceContext';

interface PatternEpicEditorProps {
  readonly node: ContentNode;
  readonly contentId: string;
  /** Author guidance rendered beneath the section title. */
  readonly description?: string;
  /** Cloudscape "info" link node rendered inline next to the section title. */
  readonly info?: ReactNode;
}

export const PatternEpicEditor = memo(function PatternEpicEditor({
  node,
  contentId,
  description,
  info,
}: PatternEpicEditorProps) {
  const vm = usePatternEpicEditorViewModel();
  const navigationService = useSuggestionNavigationService();

  // Delete modal state
  const [deleteEpicId, setDeleteEpicId] = useState<string | undefined>(undefined);
  const [deleteEpicTitle, setDeleteEpicTitle] = useState('');

  const openDeleteModal = useCallback(
    (epicId: string) => {
      const epic = vm?.epics.find((e) => e.id === epicId);
      setDeleteEpicTitle(typeof epic?.title === 'string' ? epic.title : '');
      setDeleteEpicId(epicId);
    },
    [vm?.epics],
  );

  const confirmDelete = useCallback(() => {
    if (deleteEpicId) vm?.deleteEpic(deleteEpicId);
    setDeleteEpicId(undefined);
  }, [deleteEpicId, vm]);

  const dismissDelete = useCallback(() => {
    setDeleteEpicId(undefined);
  }, []);

  const keyExtractor = useCallback((epic: Epic) => epic.id, []);

  // When the "edit" modal is being displayed, user can navigate between inline
  // edit dialogs and comments. If they navigate outside of the current epic,
  // close it immediately.
  useEffect(() => {
    return navigationService.on('navigationItemChanged', ({ item }) => {
      if (!vm?.editModal) {
        return;
      }
      const isNavigatedWithinCurrentEpic =
        // Navigated to a Tiptap editor instance's inline edit or comment
        (item.type === 'doc_inline_edit' || item.type === 'doc_inline_comment') &&
        // The editor instance must be for an epic description or title
        (item.documentId.type === 'epic_description' || item.documentId.type === 'epic_title') &&
        // And whenever the epic navigated to is the same as the one being edited
        item.documentId.epicId === vm.editModal.epicId;

      if (!isNavigatedWithinCurrentEpic) {
        vm.dismissEditModal();
      }
    });
  }, [navigationService, vm, vm?.editModal]);

  const renderEpic = useCallback(
    (epic: Epic, { setNodeRef, style, dragHandleProps, isDragging }: SortableRenderArgs) => {
      const suggestions = vm?.epicInlineSuggestions.get(epic.id);
      return (
        <div ref={setNodeRef} style={style}>
          <PatternEpic
            epic={epic}
            newlyAddedTaskId={vm?.newlyAddedTaskId}
            contentId={contentId}
            hasTitleInlineSuggestions={suggestions?.hasTitleInlineSuggestions ?? false}
            hasDescriptionInlineSuggestions={suggestions?.hasDescriptionInlineSuggestions ?? false}
            onEdit={() => vm?.openEditModal(epic.id)}
            onDelete={() => openDeleteModal(epic.id)}
            onAddTask={() => vm?.addTaskToEpic(epic.id)}
            onReorderTasks={(ids) => vm?.reorderTasksInEpic(epic.id, ids)}
            dragHandleProps={dragHandleProps}
            isDragging={isDragging}
          />
        </div>
      );
    },
    [vm, openDeleteModal, contentId],
  );

  if (!vm) return null;

  return (
    <Container
      header={
        <Header
          variant="h3"
          info={info}
          description={
            description && (
              <Box variant="span" color="text-body-secondary" fontSize="body-s">
                {description}
              </Box>
            )
          }
        >
          {node.title}
        </Header>
      }
    >
      <SpaceBetween size="m">
        {vm.epics.length > 0 ? (
          [
            <SortableList
              key="epics"
              items={vm.epics as Epic[]}
              keyExtractor={keyExtractor}
              onReorder={vm.reorderEpics}
              item={renderEpic}
              container={(children: ReactNode) => <SpaceBetween size="m">{children}</SpaceBetween>}
            />,
            <Button key="add-epic" iconName="add-plus" onClick={vm.addEpic}>
              Add epic
            </Button>,
          ]
        ) : (
          <Box textAlign="center">
            <Button iconName="add-plus" onClick={vm.addEpic}>
              Add epic
            </Button>
          </Box>
        )}
      </SpaceBetween>

      {/* Edit epic modal */}
      {vm.editModal && (
        <Modal
          visible
          header="Edit Epic"
          onDismiss={vm.dismissEditModal}
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={vm.dismissEditModal}>
                  Cancel
                </Button>
                <Button variant="primary" disabled={!vm.isSaveEnabled} onClick={vm.saveEditModal}>
                  Save
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween direction="vertical" size="m">
            <TitleInput
              label="Title"
              initialTitle={vm.editModal.title}
              minLength={EPIC_TITLE_MIN_LENGTH}
              maxLength={EPIC_TITLE_MAX_LENGTH}
              onTitleChanged={vm.updateEditTitle}
              autoFocus
              inlineTextFilter={{ type: 'epic_title', epicId: vm.editModal.epicId }}
              onCommentReply={vm.dismissEditModal}
            />
            <TitleInput
              label="Description (optional)"
              initialTitle={vm.editModal.description}
              minLength={EPIC_DESCRIPTION_MIN_LENGTH}
              maxLength={EPIC_DESCRIPTION_MAX_LENGTH}
              onTitleChanged={vm.updateEditDescription}
              inlineTextFilter={{ type: 'epic_description', epicId: vm.editModal.epicId }}
              onCommentReply={vm.dismissEditModal}
            />
          </SpaceBetween>
        </Modal>
      )}

      {/* Delete confirmation modal */}
      {deleteEpicId && (
        <ConfirmationModalView
          header="Delete this epic?"
          body={`The epic "${deleteEpicTitle || 'Untitled'}" and all its stories will be removed. This cannot be undone.`}
          confirmLabel="Delete"
          isConfirming={false}
          onConfirm={confirmDelete}
          onDismiss={dismissDelete}
        />
      )}
    </Container>
  );
});
